import React from 'react';
import { ColumnDef, CellContext } from '@tanstack/react-table';
import type { Bond } from '../types/bonds';
import { formatDate } from '../utils/dateUtils';
import { BOND_CONSTANTS } from '../constants/bonds';

// Interface for table cell props
export interface EditableCellProps {
  value: unknown;
  onSave: (value: unknown) => void;
  displayValue?: string;
  isDate?: boolean;
}

// Interface for date picker cell props
export interface DatePickerCellProps {
  value: string;
  onSave: (value: string) => void;
  displayValue?: string;
}

// Function to create table columns
export const createBondsTableColumns = (
  periodsForSelectedTab: string[],
  handleCellEdit: (rowIndex: number, columnId: string, value: unknown) => void,
  handleDeleteRow: (rowIndex: number) => void,
  EditableCell: React.ComponentType<EditableCellProps>,
): ColumnDef<Bond, unknown>[] => [
    {
      accessorKey: 'ticker',
      header: 'Symbol',
      cell: (info) => <EditableCell value={info.getValue()} onSave={(value) => handleCellEdit(info.row.index, 'ticker', value)} />,
    },
    {
      accessorKey: 'type',
      header: 'Typ',
      cell: (info) => <EditableCell value={info.getValue()} onSave={(value) => handleCellEdit(info.row.index, 'type', value)} />,
    },
    {
      accessorKey: 'sellingStartDate',
      header: 'Początek sprzedaży',
      cell: (info) => {
        const value = info.getValue();
        const dateValue = typeof value === 'string' ? value : '';
        return <EditableCell value={dateValue} displayValue={formatDate(dateValue)} onSave={(value) => handleCellEdit(info.row.index, 'sellingStartDate', value)} isDate />;
      },
    },
    {
      accessorKey: 'sellingEndDate',
      header: 'Koniec sprzedaży',
      cell: (info) => {
        const value = info.getValue();
        const dateValue = typeof value === 'string' ? value : '';
        return <EditableCell value={dateValue} displayValue={formatDate(dateValue)} onSave={(value) => handleCellEdit(info.row.index, 'sellingEndDate', value)} isDate />;
      },
    },
    {
      accessorKey: 'bondDurationInMonths',
      header: 'Czas trwania (miesiące)',
      cell: (info) => <EditableCell value={info.getValue()} onSave={(value) => handleCellEdit(info.row.index, 'bondDurationInMonths', value)} />,
    },
    {
      accessorKey: 'periodDurationInMonths',
      header: 'Długość okresu (miesiące)',
      cell: (info) => <EditableCell value={info.getValue()} onSave={(value) => handleCellEdit(info.row.index, 'periodDurationInMonths', value)} />,
    },
    // Dynamic interest rate columns for each period
    ...periodsForSelectedTab.map((period) => ({
      id: `period_${period}`,
      accessorFn: (row: Bond) => {
        const periodData = row.interestRates[period];
        if (typeof periodData === 'number') {
          return periodData;
        }
        return null;
      },
      header: `Okres ${period}`,
      cell: (info: CellContext<Bond, unknown>) => {
        const value = info.getValue();
        if (value !== null && typeof value === 'number') {
          const displayValue = `${(value * 100).toFixed(2)}%`;
          return (
            <EditableCell
              value={displayValue}
              displayValue={displayValue}
              onSave={(editValue) => {
                // Remove all non-digit and non-dot characters
                if (typeof editValue === 'string') {
                  const cleanValue = editValue.replace(/[^\d.]/g, '');
                  const numericValue = parseFloat(cleanValue);
                  if (!isNaN(numericValue)) {
                    handleCellEdit(info.row.index, `period_${period}`, numericValue);
                  }
                }
              }}
            />
          );
        }
        return (
          <EditableCell
            value="0.00%"
            displayValue="0.00%"
            onSave={(editValue) => {
              // Remove all non-digit and non-dot characters
              if (typeof editValue === 'string') {
                const cleanValue = editValue.replace(/[^\d.]/g, '');
                const numericValue = parseFloat(cleanValue);
                if (!isNaN(numericValue)) {
                  handleCellEdit(info.row.index, `period_${period}`, numericValue);
                }
              }
            }}
          />
        );
      },
    })),
    {
      id: 'cumulatedValue',
      accessorFn: (row: Bond) => {
        if (BOND_CONSTANTS.CUMULATED_VALUE_KEY in row.interestRates && typeof row.interestRates[BOND_CONSTANTS.CUMULATED_VALUE_KEY] === 'number') {
          return row.interestRates[BOND_CONSTANTS.CUMULATED_VALUE_KEY];
        }
        return null;
      },
      header: 'Wartość skumulowana',
      cell: (info) => {
        const value = info.getValue();
        if (value !== null && typeof value === 'number') {
          return <EditableCell value={value.toString()} displayValue={value.toString()} onSave={(editValue) => handleCellEdit(info.row.index, 'cumulatedValue', editValue)} />;
        }
        return <EditableCell value="0" displayValue="0" onSave={(editValue) => handleCellEdit(info.row.index, 'cumulatedValue', editValue)} />;
      },
    },
    {
      id: 'actions',
      header: 'Akcje',
      cell: (info) => (
        <button onClick={() => handleDeleteRow(info.row.index)} className="delete-button" title="Usuń wiersz">
          🗑️
        </button>
      ),
    },
  ];

// Table configuration constants
export const TABLE_CONFIG = {
  emptyMessage: {
    title: 'Brak obligacji do wyświetlenia.',
    subtitle: 'Kliknij "Dodaj nowy wiersz", aby rozpocząć.',
  },
  hints: {
    editHint: '💡 Kliknij na dowolną komórkę, aby ją edytować',
  },
  buttons: {
    addRow: '➕ Dodaj nowy wiersz',
    save: '💾 Zapisz zmiany',
  },
};
